import { autoinject, TaskQueue, bindable } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService, IAuthStatusSearchFilters } from 'app/um/services/auth-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { ExpandEvent, EssTableCustomElement } from 'app/shared/elements/ess-table';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { validate } from 'app/shared/elements/ess-rule';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { localSort } from 'app/shared/lib/sorting';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { AuthStatusReasonReloadEvent } from 'app/um/lib/events';
import { IPopup } from 'app/shared/elements/popup-base';
import { SaveReason } from 'app/um/components/auth-statuses/save-reason';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import * as UM from 'app/um/models/essette-api';

const expandedReasonsCache = new Map<string, Array<UM.AuthStatus>>();

@autoinject()
export class StatusReasons extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected expandedCacheKey: string = "um-manage-auth-statuses-reasons";

    @bindable
    protected rememberExpand: boolean = true;

    private authStatusReasonReload: Subscription | null = null;

    protected authStatusReason: UM.AuthStatusReason | null = null;
    protected authStatuses: ReadonlyArray<UM.AuthStatus> | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number | null = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;

    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<UM.AuthStatus[]>;
    protected $pager!: EssPagerCustomElement;

    protected $saveReasonModal!: IPopup;
    protected $form!: IForm;
    protected $reasonForm!: Element;
    protected $saveReason!: SaveReason;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        private readonly queue: TaskQueue,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    protected attached(): void {
        this.authStatusReasonReload = this.events.subscribe(AuthStatusReasonReloadEvent, (e: AuthStatusReasonReloadEvent) => {
            this.reload();
        });
    }

    protected detached(): void {
        if(this.authStatusReasonReload) {
            this.authStatusReasonReload.dispose();
        }
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected applyLocalSort(event: SortChangeEvent, reasons: (UM.AuthStatusReason)[]): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        localSort(reasons, sorting);
        if(this.sorting.entries.length == 0) {
            this.reload();
        };
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent) {
        this.$saveReasonModal.close();

        if(detail.form.reset) detail.form.reset();

        // reload grid and reset page
        this.reload();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadAuthStatuses();
    }

    protected saveAuthStatusReason(reason: UM.AuthStatusReason): void {
        this.$saveReason.reason = reason;
        this.authStatusReason = reason;
        this.$saveReasonModal.open();
    }

    protected saveAuthStatusReasonClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$saveReasonModal.close();
    }

    private restoreExpanded() {
        if(!this.rememberExpand) { return; }
        // we need to give aurelia a chance to bind the reasons into the
        // table before we ask the table to expand the rows
        this.queue.queueTask(() => {
            if(!this.expandedCacheKey) { return; }
            const expandedReasons = expandedReasonsCache.get(this.expandedCacheKey);
            if(expandedReasons !== undefined) {
                this.$table.restoreExpanded(expandedReasons);
            }
        });
    }

    protected trackExpanded({ detail }: ExpandEvent<UM.AuthStatus>) {
        if(!this.rememberExpand || !this.expandedCacheKey) { return; }
        expandedReasonsCache.set(this.expandedCacheKey, detail.items);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async activateAuthStatusReason(authStatusReason: UM.AuthStatusReason): Promise<void> {
        // Show confirmation dialog only if deactivating
        if(authStatusReason.isActive) {
            const result = await confirmation.showDeactivate('um:auth.status.reason.$long', 1);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateAuthStatusReason(authStatusReason.id!, !authStatusReason.isActive);
            isSuccessful = response.isSuccessful;
            this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('um:auth.status.reason.$long', !authStatusReason.isActive, isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadAuthStatuses(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting,
            includeInactive: this.includeInactive
        };

        const searchFilters: IAuthStatusSearchFilters = { withReasons: true };

        try {
            this.state.beginLoad();

            const response = await this.service.loadAuthStatuses(options, [], searchFilters);
            this.authStatuses = response.results;
            this.resultCount = response.count || 0;
            this.restoreExpanded();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

}
